import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { NAATI_FAQ_CATEGORIES } from "@/content/naati-corpus/faq-defs";

export const Route = createFileRoute("/naati/faq/")({
  loader: async (): Promise<{ NAATI_FAQ_TH: { q: string; a: string }[]; NAATI_KEYWORDS_TH: string[] }> => {
    const [{ NAATI_FAQ_TH }, { NAATI_KEYWORDS_TH }] = await Promise.all([
      import("@/content/naati-corpus/faq-th"),
      import("@/content/naati-corpus/keywords-th"),
    ]);
    return { NAATI_FAQ_TH, NAATI_KEYWORDS_TH };
  },
  component: NaatiFaqHub,
  head: ({ loaderData }) => {
    const { NAATI_FAQ_TH = [], NAATI_KEYWORDS_TH = [] } = loaderData ?? {};
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `NAATI แปลรับรอง FAQ ${NAATI_FAQ_TH.length} ข้อ 2026 — วีซ่าออสเตรเลีย/นิวซีแลนด์`,
        description:
          "รวมคำถามที่พบบ่อยเรื่องแปลรับรอง NAATI ครบทุกหมวด — เอกสารทะเบียนราษฎร การศึกษา การเงิน วีซ่าคู่ครอง วีซ่านักเรียน/ทำงาน ราคา และพื้นที่ให้บริการ อัปเดต 2026",
        path: "/naati/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/naati/faq" }],
        keywords: NAATI_KEYWORDS_TH.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "NAATI Translation", url: `${site}/naati` },
              { name: "NAATI FAQ", url: `${site}/naati/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(NAATI_FAQ_TH)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: NAATI_KEYWORDS_TH.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/naati/faq")) },
      ],
    };
  },
});

function NaatiFaqHub() {
  const { NAATI_FAQ_TH, NAATI_KEYWORDS_TH } = Route.useLoaderData() as { NAATI_FAQ_TH: { q: string; a: string }[]; NAATI_KEYWORDS_TH: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/naati" className="hover:underline">NAATI Translation</a> <span>/</span> <span>FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          NAATI แปลรับรอง — FAQ {NAATI_FAQ_TH.length} ข้อ (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          คำตอบครบทุกเรื่องเกี่ยวกับแปลรับรอง NAATI สำหรับยื่นวีซ่าออสเตรเลีย นิวซีแลนด์ แคนาดา และ UK —
          เอกสารทะเบียนราษฎร การศึกษา การเงิน หลักฐานความสัมพันธ์ ราคา และพื้นที่ให้บริการทั่วประเทศไทย
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/naati/faq" className="text-primary underline">
            NAATI translation FAQ for visa applicants →
          </a>
        </div>

        <section aria-label="หมวดคำถาม NAATI" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">หมวดคำถาม</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NAATI_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/naati/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleTh} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {NAATI_FAQ_TH.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ให้ทีมนักแปล NAATI ดูแลเอกสารครบวงจร</h2>
          <p className="text-sm text-muted-foreground mb-4">
            รับเอกสาร → แปลรับรอง NAATI → ตรวจทาน → ส่งไฟล์ PDF/EMS → ยื่น DHA / INZ / IRCC
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ขอใบเสนอราคา →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">คำค้นที่เกี่ยวข้อง ({NAATI_KEYWORDS_TH.length} คำค้น)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_KEYWORDS_TH.slice(0, 300).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>

        <PageGallery category="naati" seed="naati-faq-index" locale="th" />
        <FaqSourceNote topic="translation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
